// @refresh reset — dev-only: force remount so the useMemo([]) board nodes hot-reload on edit (no prod effect)
import { useMemo, type ReactNode } from "react";
import { type Node, type NodeTypes } from "@xyflow/react";
import { BoardCanvas } from "./BoardCanvas";
import { SectionBox, SectionGrid } from "./SectionBox";
import { nodeTypes } from "./marketing/nodes";
import { band2Website } from "./marketing/bands/band2-website";
import { band3Assets } from "./marketing/bands/band3-assets";
import { band5Articles } from "./marketing/bands/band5-articles";
import { band6TopicAnalysis } from "./marketing/bands/band6-topic-analysis";
import { band7References } from "./marketing/bands/band7-section-library";

// ─────────────────────────────────────────────────────────────────────────────
// MARKETING RESEARCH BOARD — refactored to the LOCKED board-container standard
// (SectionBox + SectionGrid, Erez 2026-07-19). The research/reference material:
// Article Research, Homepage Teardowns, Topic Analysis, Assets & Design, and the
// Relevance AI reference pages.
//
// Every section is one <SectionBox> (big black label, neutral-gray container);
// its real content is rendered as WHITE cards by REUSING the existing marketing
// node components (siteFrame, teardown, articleShot, logoCategory, galleryBox,
// iconGallery, analysisCard, …) as plain inline React — those are the same
// white cards the old canvas drew, now wrapped in the standard container. ALL
// SectionBoxes live inside ONE <SectionGrid> in ONE React Flow node → a wide
// rectangle, rendered through <BoardCanvas>. Nothing lives outside a SectionBox.
// Content + data are untouched (pulled straight from the shared band modules). Leo.
// ─────────────────────────────────────────────────────────────────────────────

const REF_HASH = "https://bae7f8a8.tailormind-site.pages.dev";

// Render one marketing band-node as a plain inline card (no React Flow node wrapper).
// The node components only read `data`, so feeding them `{ data }` is all they need.
function NodeView({ node }: { node: Node }) {
  const Comp = (nodeTypes as any)[node.type as string] as
    | ((p: { data: any }) => ReactNode)
    | undefined;
  return Comp ? <Comp data={node.data} /> : null;
}

// ── Group each band's flat node list back into its content units ──────────────

// Article Research → per-company columns (header + its real captured article shots).
function articleColumns(): { header: Node; shots: Node[] }[] {
  const cols: { header: Node; shots: Node[] }[] = [];
  for (const n of band5Articles()) {
    if (n.type === "articleColHeader") cols.push({ header: n, shots: [] });
    else if (n.type === "articleShot" && cols.length) cols[cols.length - 1].shots.push(n);
  }
  return cols;
}

// Homepage teardowns → per-site units: title label + the REAL homepage capture
// (siteFrame) beside our section-by-section analysis (teardown card).
function teardownUnits(bust: string): { label?: Node; shot?: Node; card?: Node }[] {
  const nodes = band2Website(bust).filter(
    (n) => String(n.id).startsWith("td-") && n.id !== "td-zone",
  );
  const byI = new Map<string, { label?: Node; shot?: Node; card?: Node }>();
  for (const n of nodes) {
    const id = String(n.id);
    const m = id.match(/^td-(\d+)/);
    if (!m) continue;
    const u = byI.get(m[1]) || {};
    if (id.endsWith("-label")) u.label = n;
    else if (id.endsWith("-shot")) u.shot = n;
    else u.card = n;
    byI.set(m[1], u);
  }
  return [...byI.values()];
}

// Relevance AI references → per-page units: title label + full-page capture.
function relevanceUnits(bust: string): { label?: Node; frame?: Node }[] {
  const nodes = band7References(REF_HASH, bust).filter((n) => n.id !== "seclib-refzone");
  const byR = new Map<string, { label?: Node; frame?: Node }>();
  const PREFIX = "seclib-ref-";
  for (const n of nodes) {
    const id = String(n.id);
    const isLabel = id.endsWith("-label");
    const r = isLabel ? id.slice(PREFIX.length, -"-label".length) : id.slice(PREFIX.length);
    const u = byR.get(r) || {};
    if (isLabel) u.label = n;
    else u.frame = n;
    byR.set(r, u);
  }
  return [...byR.values()];
}

// Assets & Design → the logo directions, hero/stock galleries, and icon library.
// Drop the band's own big section titles (SectionBox supplies the one label).
function assetNodes(bust: string): Node[] {
  return band3Assets(bust).filter((n) => n.type !== "sectionTitle");
}

// ── The ONE node: every SectionBox packed into one SectionGrid → wide rectangle ─

function BoardGridNode({ data }: { data: { bust: string } }) {
  const { bust } = data;
  const cols = articleColumns();
  const teardowns = teardownUnits(bust);
  const refs = relevanceUnits(bust);
  const assets = assetNodes(bust);
  const topic = band6TopicAnalysis()[0];

  return (
    <SectionGrid>
      {/* Article Research — one horizontal wall of per-publisher columns */}
      <SectionBox title="Article Research" layout="custom">
        <div style={{ display: "flex", gap: 80, alignItems: "flex-start" }}>
          {cols.map((c, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", gap: 40, width: 440 }}>
              <NodeView node={c.header} />
              {c.shots.map((s, j) => (
                <NodeView key={j} node={s} />
              ))}
            </div>
          ))}
        </div>
      </SectionBox>

      {/* Homepage Teardowns — real capture + our analysis, side by side, in a row */}
      <SectionBox title="Homepage Teardowns" layout="custom">
        <div style={{ display: "flex", gap: 120, alignItems: "flex-start" }}>
          {teardowns.map((u, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {u.label && <NodeView node={u.label} />}
              <div style={{ display: "flex", gap: 60, alignItems: "flex-start" }}>
                {u.shot && <NodeView node={u.shot} />}
                {u.card && <NodeView node={u.card} />}
              </div>
            </div>
          ))}
        </div>
      </SectionBox>

      {/* Topic Analysis — the single synthesis card */}
      <SectionBox title="Topic Analysis" layout="custom">
        {topic ? <NodeView node={topic} /> : null}
      </SectionBox>

      {/* Assets & Design — logo directions, hero/stock galleries, icon library */}
      <SectionBox title="Assets &amp; Design" layout="custom">
        <div
          style={{ display: "flex", flexWrap: "wrap", gap: 64, width: 5600, alignItems: "flex-start" }}
        >
          {assets.map((n, i) => (
            <NodeView key={i} node={n} />
          ))}
        </div>
      </SectionBox>

      {/* Relevance AI Reference — full-page captures of the richer content style */}
      <SectionBox title="Relevance AI Reference" layout="custom">
        <div style={{ display: "flex", gap: 80, alignItems: "flex-start" }}>
          {refs.map((u, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", gap: 20, width: 760 }}>
              {u.label && <NodeView node={u.label} />}
              {u.frame && <NodeView node={u.frame} />}
            </div>
          ))}
        </div>
      </SectionBox>
    </SectionGrid>
  );
}

const boardNodeTypes: NodeTypes = { boardGrid: BoardGridNode as any };

export function MarketingResearchBoardPage() {
  const nodes = useMemo<Node[]>(
    () => [
      {
        id: "board-grid",
        type: "boardGrid",
        position: { x: 0, y: 0 },
        data: { bust: "?v=2026-07-20" },
        draggable: false,
        selectable: false,
      },
    ],
    [],
  );

  return <BoardCanvas nodes={nodes} nodeTypes={boardNodeTypes} />;
}
